import { useState } from 'react';
import { FormField, Grid } from '@amzn/awsui-components-core';
import SimpleRichTextInput from '../../common/SimpleRichTextInput';
import InputSizeMessage from '../../utils/InputSizeMessage';
import CharacterCountMessage from '../../utils/CharacterCountMessage';

interface RichTextDescriptionFieldProps {
  value: string;
  onChange: (value: string) => void;
  minLength: number;
  maxLength: number;
  label?: string | null;
  /** Called when the field's validity changes based on character count. */
  onValidationChange?: (isValid: boolean) => void;
}

/**
 * A form field that renders a rich text editor.
 *
 * Validates against plain-text character count (HTML tags excluded) so users
 * see the same limits regardless of formatting.
 *
 * The field owns its own character count state and notifies the parent of
 * validity changes via `onValidationChange`.
 */
const RichTextDescriptionField = ({
  value,
  onChange,
  minLength,
  maxLength,
  label = 'Description',
  onValidationChange,
}: RichTextDescriptionFieldProps) => {
  const [richTextCharCount, setRichTextCharCount] = useState(0);

  const plainTextLength = richTextCharCount;
  const isCountValid = (count: number) => count >= minLength && count <= maxLength;

  const handleCharacterCountChange = (count: number) => {
    setRichTextCharCount(count);
    onValidationChange?.(isCountValid(count));
  };

  return (
    <FormField label={label} i18nStrings={{ errorIconAriaLabel: 'Error' }} stretch>
      <SimpleRichTextInput
        value={value}
        onChange={onChange}
        onCharacterCountChange={handleCharacterCountChange}
      />
      <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
        <InputSizeMessage
          input={'x'.repeat(plainTextLength)}
          minNum={minLength}
          maxNum={maxLength}
        />
        <CharacterCountMessage input={'x'.repeat(plainTextLength)} />
      </Grid>
    </FormField>
  );
};

export default RichTextDescriptionField;
